import { Loader2Icon, MapPinIcon, SearchIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface SuggestedAddress {
  street: string
  city: string
  zip: string
  country: string
}

export interface AddressSuggestProps {
  value: string
  onChange: (value: string) => void
  onSelect: (address: SuggestedAddress) => void
  placeholder?: string
  className?: string
}

type MapySuggestItem = {
  name: string
  label: string
  zip?: string
  location: string
  regionalStructure: { name: string; type: string; isoCode?: string }[]
}

const DEBOUNCE_MS = 400
const MIN_SEARCH_LENGTH = 3

function toSuggestedAddress(item: MapySuggestItem): SuggestedAddress {
  return {
    street: item.name,
    city:
      item.regionalStructure.find((r) => r.type === 'regional.municipality')
        ?.name ?? '',
    zip: item.zip ?? '',
    country:
      item.regionalStructure.find((r) => r.type === 'regional.country')
        ?.isoCode ?? 'CZ',
  }
}

export const AddressSuggest = ({
  value,
  onChange,
  onSelect,
  placeholder,
  className,
}: AddressSuggestProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'addressSuggest' })
  const [results, setResults] = useState<MapySuggestItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchPerformed, setSearchPerformed] = useState(false)
  const [open, setOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const skipNextSearchRef = useRef(false)
  const abortRef = useRef<AbortController | null>(null)

  const fetchSuggestions = useCallback(async (query: string) => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setIsLoading(true)
    try {
      const url = new URL('https://api.mapy.com/v1/suggest')
      url.searchParams.set('lang', 'cs')
      url.searchParams.set('limit', '6')
      url.searchParams.set('type', 'regional.address')
      url.searchParams.set('apikey', import.meta.env.VITE_MAPY_API_KEY)
      url.searchParams.set('query', query)
      const res = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      })
      if (!res.ok) throw new Error(`Mapy suggest failed: ${res.status}`)
      const data = (await res.json()) as { items?: MapySuggestItem[] }
      setResults(data.items ?? [])
      setHighlightedIndex(0)
      setSearchPerformed(true)
      setOpen(true)
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      setResults([])
      setSearchPerformed(true)
    } finally {
      if (abortRef.current === controller) {
        setIsLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false
      return
    }
    const trimmed = value.trim()
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      setResults([])
      setSearchPerformed(false)
      setOpen(false)
      return
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(trimmed)
    }, DEBOUNCE_MS)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [value, fetchSuggestions])

  const selectItem = useCallback(
    (item: MapySuggestItem) => {
      skipNextSearchRef.current = true
      onSelect(toSuggestedAddress(item))
      setOpen(false)
      setResults([])
      setSearchPerformed(false)
    },
    [onSelect],
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => (i + 1) % results.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => (i - 1 + results.length) % results.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      selectItem(results[highlightedIndex])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.querySelector(
        `[data-index="${highlightedIndex}"]`,
      )
      el?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex, open])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={cn('relative', className)} onKeyDown={handleKeyDown}>
          <Input
            placeholder={placeholder}
            autoComplete="off"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => {
              if (results.length > 0 || searchPerformed) setOpen(true)
            }}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
            {isLoading ? (
              <Loader2Icon className="size-4 animate-spin" />
            ) : (
              <SearchIcon className="size-4" />
            )}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div ref={listRef} className="max-h-[320px] overflow-auto">
          {results.length === 0 && !isLoading && searchPerformed && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {t('notFound')}
            </div>
          )}
          {results.length > 0 && (
            <div className="flex flex-col gap-0.5 py-2">
              {results.map((item, index) => (
                <Button
                  key={`${item.label}-${item.location}-${index}`}
                  type="button"
                  variant="ghost"
                  className="flex items-start gap-2 justify-start h-auto py-2 px-3 rounded-none"
                  data-index={index}
                  data-highlighted={index === highlightedIndex}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => selectItem(item)}
                >
                  <MapPinIcon className="size-4 shrink-0 mt-0.5 text-muted-foreground" />
                  <div className="flex flex-col gap-0.5 text-left min-w-0">
                    <span className="font-medium truncate">{item.name}</span>
                    <span className="text-xs text-muted-foreground truncate">
                      {item.location}
                    </span>
                  </div>
                </Button>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

AddressSuggest.displayName = 'AddressSuggest'
